import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import {
  initialLegalEntityIndividualContactFormValues,
  initialLegalEntityOrganizationContactFormValues,
} from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ILegalEntityContact } from 'models/legalEntity';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';
import { useDictionary } from 'models/dictionaryContext';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useTranslations } from 'hooks/useTranslations';
import { useLEContactSchema } from 'pages/LegalEntitiesPage/schemaValidation';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { useScroll } from 'hooks/useScroll';
import { Box } from '@mui/system';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { ERouteLinks } from 'models/route';
import { mapBlankToNull } from 'utils/form';
import { ESelectLegalEntityContext } from '../SelectLegalEntityPage';

export interface ILegalEntityContactFormValues {
  uniqueId: string;
  name?: string;
  role?: string;
  email: string;
  codeDictionaryUniqueId: string;
  value: string;
  associatedIndividualUniqueId?: string;
  associatedIndividualName?: string;
}

export interface ILegalEntityForm {
  onCancel?: () => void;
  onSubmit: (arg: ILegalEntityContactFormValues | any) => void;
  initialFormValues?: ILegalEntityContact | ILegalEntityContactFormValues | null;
  type: ELegalEntityTypes;
  isEdit: boolean;
}

const LegalEntityContactForm = ({
  onCancel,
  onSubmit,
  initialFormValues,
  type,
  isEdit,
}: ILegalEntityForm) => {
  const { id } = useParams<{ id: string }>();
  const { state: routerState }: any = useLocation();
  const { loadDictionaries, legalEntityLatestContact, getLegalEntityLatestContactData } =
    useLegalEntities();
  const { phoneCode } = useDictionary();
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { leContactSchema } = useLEContactSchema(type);
  const { elRef, scrollToError } = useScroll();
  const { setAlert, clearAlert, samePageAlert } = useAlert();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    getValues,
    watch,
  } = useForm<ILegalEntityContact | ILegalEntityContactFormValues>({
    defaultValues:
      initialFormValues || type === ELegalEntityTypes.individual
        ? initialLegalEntityIndividualContactFormValues
        : initialLegalEntityOrganizationContactFormValues,
    resolver: yupResolver(leContactSchema) as unknown as Resolver<
      ILegalEntityContact | ILegalEntityContactFormValues
    >,
  });

  const associatedIndividualName = watch('associatedIndividualName');
  const associatedIndividualUniqueId = watch('associatedIndividualUniqueId');

  useEffect(() => {
    if (routerState?.form) {
      reset({
        ...getValues(),
        ...routerState.form,
      });
    } else {
      reset(
        initialFormValues ||
        initialLegalEntityIndividualContactFormValues ||
        initialLegalEntityOrganizationContactFormValues,
      );
    }
  }, [reset, routerState]);

  useEffect(() => {
    (async () => {
      if (associatedIndividualUniqueId && !isEdit && type === ELegalEntityTypes.organisation) {
        await getLegalEntityLatestContactData(associatedIndividualUniqueId);
      }
    })();
  }, [associatedIndividualUniqueId]);

  useEffect(() => {
    if (legalEntityLatestContact && !isEdit && type === ELegalEntityTypes.organisation) {
      setValue('email', legalEntityLatestContact.email);
      setValue('value', legalEntityLatestContact.phoneNumber?.value);
      setValue(
        'codeDictionaryUniqueId',
        legalEntityLatestContact.phoneNumber?.codeDictionaryUniqueId,
      );
    }
  }, [legalEntityLatestContact]);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const formChanged = (
    initialValues?: ILegalEntityContact | ILegalEntityContactFormValues | null,
    currentValues?: ILegalEntityContact | ILegalEntityContactFormValues | null,
  ): boolean => {
    const baseDataChanged =
      initialValues?.name !== currentValues?.name ||
      initialValues?.email !== currentValues?.email ||
      initialValues?.role !== currentValues?.role ||
      initialValues?.associatedIndividualName !== currentValues?.associatedIndividualName;

    if (initialValues && Object.prototype.hasOwnProperty.call(initialValues, 'phoneNumber')) {
      const initialContact = initialValues as ILegalEntityContact;
      const currentContact = currentValues as ILegalEntityContact;
      const contactDataChanged =
        initialContact.phoneNumber?.codeDictionaryUniqueId !==
        currentContact.phoneNumber?.codeDictionaryUniqueId ||
        initialContact.phoneNumber?.value !== currentContact.phoneNumber?.value;
      return baseDataChanged || contactDataChanged;
    }
    const initialContact = initialValues as ILegalEntityContactFormValues;
    const currentContact = currentValues as ILegalEntityContactFormValues;
    const contactDataChanged =
      initialContact?.codeDictionaryUniqueId !== currentContact?.codeDictionaryUniqueId ||
      mapBlankToNull(initialContact?.value) !== currentContact?.value;
    return baseDataChanged || contactDataChanged;
  };

  const onSubmitForm = async (data: any) => {
    if (!formChanged(initialFormValues, data)) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
      return;
    }
    setIsSubmitLoading(true);
    if (initialFormValues) {
      onSubmit({
        ...initialFormValues,
        ...data,
      });
      setIsSubmitLoading(false);
    } else {
      onSubmit({
        uniqueId: id as string,
        name: data.name,
        email: data.email,
        role: data.role,
        phoneNumber: data.codeDictionaryUniqueId
          ? {
            codeDictionaryUniqueId: data.codeDictionaryUniqueId,
            value: data.value,
          }
          : undefined,
        associatedIndividualUniqueId: data?.associatedIndividualUniqueId,
      });
      setIsSubmitLoading(false);
    }
    setIsSubmitLoading(false);
  };

  useEffect(() => {
    (async function init() {
      await loadDictionaries([EDictionaryTypes.PhoneCode]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Grid container ref={elRef}>
        <Grid size={{ xs: 12, lg: 9 }} container columnSpacing={2}>
          {type === ELegalEntityTypes.organisation && (
            <>
              <Grid size={{ xs: 12, lg: 6 }} sx={{ marginTop: { lg: '28px' } }}>
                <Button
                  id="legal_entity_details_contact_search_for_legal-button"
                  fullWidth
                  label={t('addContact.searchLegalEntity.label')}
                  variant={EButtonVariants.contained}
                  size={EButtonSizes.small}
                  onClick={() => {
                    if (id) {
                      navigate(
                        `${ERouteLinks.SelectLegalEntity
                        }?limitTo=${ELegalEntityTypes.individual.toUpperCase()}&expand=${ELegalEntityTypes.individual
                        }`,
                        {
                          state: {
                            form: getValues(),
                            context: ESelectLegalEntityContext.ASSOCIATED_INDIVIDUAL,
                            onlyWithValidLA: true,
                            returnPath: `${initialFormValues
                              ? ERouteLinks.LegalEntityEditContact.replace(
                                ':id',
                                id,
                              ).replace(':contactId', initialFormValues?.uniqueId)
                              : ERouteLinks.LegalEntityAddContact.replace(':id', id)
                              }?type=${ELegalEntityTypes.organisation}`,
                          },
                        },
                      );
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormInput
                  name="associatedIndividualName"
                  label={t('addContact.associatedIndividual.label')}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  disabled={!associatedIndividualName}
                  InputProps={{ readOnly: true }}
                  onClear={() => setValue('associatedIndividualUniqueId', '')}
                />
              </Grid>
            </>
          )}
          {type === ELegalEntityTypes.organisation && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormInput
                name="name"
                label={`${t('addContact.contactName.label')}*`}
                control={control}
                setValue={setValue}
                errors={errors}
                onClear={() => setValue('name', '')}
              />
            </Grid>
          )}
          {type === ELegalEntityTypes.organisation && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormInput
                name="role"
                label={`${t('addContact.jobTitle.label')}*`}
                setValue={setValue}
                placeholder={t('addContact.jobTitle.label')}
                control={control}
                errors={errors}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, lg: 6 }}>
            <FormInput
              name="email"
              label={`${t('addContact.emailAddress.label')}`}
              setValue={setValue}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }} container columnSpacing={2}>
            <Grid size={{ xs: 6, sm: 4, lg: 6 }}>
              <FormSearchSelect
                name="codeDictionaryUniqueId"
                options={mapDictionaryToOptionProp(phoneCode, '+')}
                label={`${t('addContact.phonePrefix.label')}`}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, lg: 6 }}>
              <FormInput
                name="value"
                label={`${t('addContact.phoneNumber.label')}`}
                setValue={setValue}
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>
          <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 3, mt: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                type="submit"
                fullWidth
                label={t('general.save.button')}
                id="save-button"
                variant={EButtonVariants.contained}
                isLoading={isSubmitLoading}
              />
            </Grid>

            {onCancel && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  id="cancel-button"
                  variant={EButtonVariants.outlined}
                  onClick={onCancel}
                />
              </Grid>
            )}
          </Grid>
          <Grid sx={{ mt: 2 }}>
            <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
          </Grid>
        </Grid>
      </Grid>
      {samePageAlert && (
        <Box sx={{ marginBottom: '10px', mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
    </form>
  );
};

export { LegalEntityContactForm };
